import React, { useCallback } from 'react';
import { observer } from 'mobx-react';
import * as yup from 'yup';
import { useParams } from 'react-router-dom';
import { Input, Spin } from 'antd';
import { useFormik } from 'formik';
import { useAuthStore, useOfferStore } from '../../contexts/mobx';
import { useUnsavedChanges } from '../../contexts/unsaved-changes';
import { OfferUpsertPayload, YupOfferUpsertPayload } from '../../stores/offer';
import { useNavigation } from '../../utils/use-navigation';
import { DeleteButton, SaveButton } from '../../components/action-buttons';
import { PageTopBar } from '../../components/page-top-bar';

// Validation for the expected params
const YupParams = yup.object({
    action: yup.string().oneOf(['create', 'edit']).required(),
    id: yup
        .string()
        .test('shouldBeDefined', 'Id should be defined', function (v) {
            const action = this.resolve(yup.ref('action'));
            return !(v === undefined && action !== 'create');
        }),
});

type Params = yup.InferType<typeof YupParams>;

export const OfferPage = observer(() => {
    const params = useParams<Params>();
    const offerStore = useOfferStore();
    const navigation = useNavigation();
    const [isDeleting, setIsDeleting] = React.useState<boolean>(false);
    const [isFetching, setIsFetching] = React.useState<boolean>(false);
    const [isUpserting, setIsUpserting] = React.useState<boolean>(false);

    const authStore = useAuthStore();

    const { hasManagementAccess } = authStore;

    if (!hasManagementAccess) {
        navigation.goToHome();
    }

    const formik = useFormik<OfferUpsertPayload>({
        initialValues: {
            items: [
                {
                    title: '',
                    language: 'en',
                },
                {
                    title: '',
                    language: 'fr',
                },
            ],
            canBeDeleted: false,
        },
        validateOnMount: true,
        validationSchema: YupOfferUpsertPayload,
        onSubmit: () => console.log('Submit!'),
    });

    const saveOffer = useCallback(async () => {
        setIsUpserting(true);
        try {
            if (params.action === 'edit') {
                await offerStore.updateOffer(formik.values, Number(params.id!));
            } else {
                await offerStore.createOffer(formik.values);
            }
        } finally {
            setIsUpserting(false);
        }
    }, [formik.values, offerStore, params.action, params.id]);

    const { clearDirty } = useUnsavedChanges({
        dirty: formik.dirty,
        onSave: saveOffer,
    });

    const createOffer = useCallback(async () => {
        await saveOffer();
        clearDirty(() => navigation.replaceToOffers());
    }, [saveOffer, clearDirty, navigation]);

    const updateOffer = useCallback(async () => {
        await saveOffer();
        clearDirty();
    }, [saveOffer, clearDirty]);

    if (!YupParams.isValidSync(params)) {
        navigation.replaceToOfferCreate();
    }

    React.useEffect(() => {
        if (params.action === 'edit') {
            setIsFetching(true);
            offerStore
                .fetchOffer(Number(params.id!))
                .then((offer) => {
                    formik.resetForm({ values: offer });
                })
                .catch(() => navigation.replaceToOfferCreate())
                .finally(() => setIsFetching(false));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onDeleteConfirm = async () => {
        setIsDeleting(true);
        const response = await offerStore.deleteOffer(Number(params.id!));
        setIsDeleting(false);

        if (!response.err) {
            navigation.replaceToOffers();
        }
    };

    const renderTopBarActions = () => {
        switch (params.action) {
            case 'create':
                return (
                    <SaveButton
                        onClick={createOffer}
                        loading={isUpserting}
                        disabled={!formik.isValid}
                    />
                );
            case 'edit':
                return (
                    <>
                        <DeleteButton
                            onClick={onDeleteConfirm}
                            loading={isDeleting}
                        />
                        <SaveButton
                            onClick={updateOffer}
                            loading={isUpserting}
                            disabled={!formik.isValid}
                        />
                    </>
                );
            default:
                return null;
        }
    };

    return (
        <>
            <PageTopBar
                title={
                    params.action === 'edit' && params.id
                        ? `Offer #${params.id}`
                        : 'New Offer'
                }
                goBack={{
                    callback: () => navigation.goToOffers(),
                    text: 'Back to list',
                }}
            >
                {renderTopBarActions()}
            </PageTopBar>

            {isFetching ? (
                <Spin />
            ) : (
                <div className="content_with_margins">
                    <form className={`small_form`}>
                        {formik.values.items.map((item, i) => (
                            <div key={item.language}>
                                <span>Title {item.language}</span>
                                <Input
                                    name={`items[${i}].title`}
                                    value={item.title}
                                    onChange={formik.handleChange}
                                />
                            </div>
                        ))}
                    </form>
                </div>
            )}
        </>
    );
});
